import { useMemo, useState } from 'react';
import { api } from '../../api/client';
import { useSaveFlow } from '../../components/confirm';
import { useModalKeys } from '../../hooks/useModalKeys';
import { useQueryClient } from '@tanstack/react-query';
import type { NotifyApi } from '../../components/notifications';
import '../accounting/accounting.css';
import { StateCitiesPanel } from '../settings/systemManagement/StateCitiesPanel';
import { useStates, useStateCountries, useSaveState, errMessage, type StateRow } from '../settings/systemManagement/systemMasters';
import type { StagedCity } from '../settings/systemManagement/city';

// ─────────────────────────────────────────────────────────────────────────────
// GoLive "State" create/edit popup, opened from the State lookup's New/Edit (✎)
// icon on the Contact / Client address forms. GoLive's popup is NOT a bare
// State + Short Name form — it carries State* / Short Name / Country PLUS the
// nested **City** grid (New · Link · Unlink · Edit · Export to), which in turn
// opens the City popup with its own Area grid. This reuses the Settings ▸ System
// Management <StateCitiesPanel> verbatim so there is ONE implementation of that
// chain (standing rule 8), not a second bespoke one.
//
// z-index: this opens ABOVE the Contact / Client modals (z 1100–2000) while the
// System-Management dialogs use `.acc-overlay` at z-index 1000 — so the bump is
// scoped to `.srn-state-host`. Confirm/alert dialogs (z 9500, portaled to the
// app root) stay above everything, so the Unlink confirm still works.
// ─────────────────────────────────────────────────────────────────────────────

export function StateDetailModal({ stateId, countryId, onClose, onSaved, notify }: {
  stateId: string | null;                // null = create new; else edit that state
  countryId: string | null;              // pre-selected Country for a new state
  onClose: () => void;
  onSaved: (savedStateId: string) => void;
  notify: NotifyApi;
}) {
  const runSave = useSaveFlow();
  const save = useSaveState();
  const qc = useQueryClient();
  const { data: states = [] } = useStates();
  const { data: countries = [] } = useStateCountries();
  const existing = useMemo<StateRow | null>(
    () => (stateId ? states.find((s) => s.id === stateId) ?? null : null),
    [stateId, states]);

  const [longName, setLongName] = useState(existing?.longName ?? '');
  const [shortName, setShortName] = useState(existing?.shortName ?? '');
  const [ctryId, setCtryId] = useState(existing?.countryId ?? countryId ?? '');
  const [err, setErr] = useState('');
  const [stagedCities, setStagedCities] = useState<StagedCity[]>([]);
  const [maxed, setMaxed] = useState(false);
  // Bespoke popup keyboard (standing rule 14): Esc closes this hand-rolled overlay.
  const modalKeys = useModalKeys(true, { onClose });

  // Save the state (create/update), commit any City→Area chain staged on a
  // brand-new state, then hand the saved id back to the caller.
  const doSave = async () => {
    if (!longName.trim()) { setErr('State must not be empty.'); return; }
    setErr('');
    const wasNew = !existing?.id;
    let newId: string | undefined;
    await runSave({
      save: async () => {
        const savedId = await save.mutateAsync({
          id: existing?.id, longName: longName.trim(),
          shortName: shortName.trim() || null, countryId: ctryId || null,
        });
        newId = existing?.id ?? savedId ?? undefined;
        // Commit cities staged on a new state → then their areas (the same
        // staged-commit CountryDetailModal does one level up).
        if (wasNew && newId && stagedCities.length) {
          for (const sc of stagedCities) {
            let cityId = sc.cityId;
            if (cityId) {
              await api.put(`/api/v1/cities/${cityId}`, { cityName: sc.name, stateId: newId });
            } else {
              const cres = await api.post('/api/v1/cities', { cityName: sc.name, stateId: newId });
              cityId = (cres as { data?: { data?: { id?: string } } })?.data?.data?.id;
            }
            if (cityId && sc.areas?.length) {
              for (const a of sc.areas) {
                if (a.areaId) await api.post(`/api/v1/cities/${cityId}/areas/link`, { areaId: a.areaId });
                else await api.post(`/api/v1/cities/${cityId}/areas`, { areaName: a.name });
              }
            }
          }
          await qc.invalidateQueries({ queryKey: ['sm-cities'] });
          await qc.invalidateQueries({ queryKey: ['sm-city-areas'] });
        }
      },
      successMessage: 'Saved successfully.',
      onSaved: () => { if (newId) onSaved(newId); else onClose(); },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  return (
    <div className="srn-state-host">
      <style>{`
        .srn-state-host .acc-overlay { z-index: 3100; }
        .srn-state-host .acc-lksel-backdrop { z-index: 3200; }
        .srn-state-host .acc-lksel-pop { z-index: 3201; }
      `}</style>
      <div className="acc-overlay" onMouseDown={onClose}>
        <div className={`acc-dialog acc-dialog-md${maxed ? ' acc-dialog-max' : ''}`} onMouseDown={(e) => e.stopPropagation()} {...modalKeys}>
          <div className="acc-dialog-hdr">
            <span>{existing ? `State - ${existing.longName ?? ''}` : 'State'}</span>
            <span className="acc-dialog-acts">
              <i className={`ti ${maxed ? 'ti-minimize' : 'ti-maximize'} acc-dialog-x`} title={maxed ? 'Restore' : 'Maximize'} onClick={() => setMaxed((m) => !m)} />
              <i className="ti ti-x acc-dialog-x" onClick={onClose} />
            </span>
          </div>
          <div className="acc-dialog-body">
            <div className="acc-field-h">
              <label className="acc-label acc-label-req">State<span className="req">*</span></label>
              <div className="acc-fh-control">
                <input className="acc-input" autoFocus value={longName}
                  onChange={(e) => { setLongName(e.target.value); if (err) setErr(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') void doSave(); }} />
                {err && <div className="acc-err">{err}</div>}
              </div>
            </div>
            <div className="acc-field-h">
              <label className="acc-label">Short Name</label>
              <div className="acc-fh-control">
                <input className="acc-input" value={shortName} onChange={(e) => setShortName(e.target.value)} />
              </div>
            </div>
            <div className="acc-field-h">
              <label className="acc-label">Country</label>
              <div className="acc-fh-control">
                <select className="acc-input" value={ctryId} onChange={(e) => setCtryId(e.target.value)}>
                  <option value="">N/A</option>
                  {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            {/* The nested State → City (→ Area) editor — GoLive parity. */}
            <StateCitiesPanel stateId={existing?.id} staged={stagedCities} onStaged={setStagedCities} />
          </div>
          <div className="acc-dialog-ftr">
            <button className="ca-btn ca-btn-solid" onClick={() => void doSave()} disabled={save.isPending}><span className="tb-i"><i className="ti ti-device-floppy" /></span> OK</button>
            <button className="ca-btn" onClick={onClose}><span className="tb-i tb-i-grey"><i className="ti ti-arrow-back-up" /></span> Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
